import React, { type PropsWithChildren, createContext, useState, useMemo, useCallback, useContext, forwardRef, useImperativeHandle } from "react";

import { useStoredData } from "@/store";
import { FixedNumber } from "@ethersproject/bignumber";

import { formatFixedNumberString } from "@/utils";

interface AmountContextState {
  totalAmount: string;
  vaultAmountMap: Record<string, string | undefined>;
  updateTotalAmount?: (value: string) => void;
  updateVaultAmount?: (vaultId: string, value: string) => void;
  getVaultAmount?: (vaultId: string) => string;
}

interface ComponentProps extends PropsWithChildren {
  spreadAmount?: (amount: string, vaultIds: string[]) => Record<string, string | undefined>;
  onVaultChange?: (vaultAmountMap: Record<string, string | undefined>, amount: string) => void;
}

export interface AmountStateInstance {
  updateTotalAmount: (value: string) => void;
  updateVaultAmount: (vaultId: string, value: string) => void;
  getVaultAmount: (vaultId: string) => string;
}

const defaultState: AmountContextState = {
  totalAmount: "",
  vaultAmountMap: {},
};

const AmountContext = createContext<AmountContextState>(defaultState);

function defaultSpreadAmount(value: string, vaultIds: string[] = []): Record<string, string | undefined> {
  const numberOfVaults = FixedNumber.from(vaultIds.length);
  const vaultAmount = !isNaN(parseFloat(value)) && !numberOfVaults.isZero() ? FixedNumber.from(value).divUnsafe(numberOfVaults).toString() : "";

  return vaultIds.reduce((acc, vaultId) => ({ ...acc, [vaultId]: formatFixedNumberString(vaultAmount) }), {});
}

export function useAmountState() {
  return useContext(AmountContext);
}

const AmountState = forwardRef<AmountStateInstance, ComponentProps>(({ children, spreadAmount = defaultSpreadAmount, onVaultChange }, ref) => {
  const [totalAmount, setTotalAmount] = useState("");
  const [vaultAmountMap, setVaultAmount] = useState<Record<string, string | undefined>>({});

  const storedData = useStoredData();

  const updateTotalAmount = useCallback(
    (value: string) => {
      const vaultIds = storedData.cart;
      const newVaultState = spreadAmount(value, vaultIds);

      setTotalAmount(value);
      setVaultAmount(newVaultState);
      onVaultChange?.(newVaultState, value);
    },
    [storedData.cart, spreadAmount, onVaultChange]
  );

  const updateVaultAmount = useCallback(
    (vaultId: string, value: string) => {
      const newVaultState = {
        ...vaultAmountMap,
        [vaultId]: value,
      };
      const total = Object.values(newVaultState)
        .filter(value => value && !isNaN(parseFloat(value)))
        .reduce((total, amount) => total.addUnsafe(FixedNumber.fromString(amount || "0")), FixedNumber.from("0"));

      setVaultAmount(newVaultState);
      setTotalAmount(formatFixedNumberString(total.toString()));
      onVaultChange?.(newVaultState, total.toString());
    },
    [vaultAmountMap, onVaultChange]
  );

  const getVaultAmount = useCallback(
    (vaultId: string) => {
      return vaultAmountMap[vaultId] ?? "";
    },
    [vaultAmountMap]
  );

  useImperativeHandle(
    ref,
    () => ({
      updateTotalAmount,
      updateVaultAmount,
      getVaultAmount,
    }),
    [getVaultAmount, updateTotalAmount, updateVaultAmount]
  );

  const context = useMemo(
    () => ({
      totalAmount,
      vaultAmountMap,
      updateTotalAmount,
      updateVaultAmount,
      getVaultAmount,
    }),
    [totalAmount, vaultAmountMap, updateTotalAmount, updateVaultAmount, getVaultAmount]
  );

  return <AmountContext.Provider value={context}>{children}</AmountContext.Provider>;
});

AmountState.displayName = "AmountState";

export default AmountState;
